import {BaseDashlet} from "../../lq_js_common/dashboard/base_dashlet";
import {DashboardGraph} from "../../graphs/dashboard_graph";
import {GraphOptionsBuilder} from "../../lq_js_common/e_charts/chart_builder";
import {MinMaxSeries} from "../../lq_js_common/e_charts/min_max_median_series";
import {createBoundedAxisTooltip, formatMillisecondsAxis, formatTimeTick} from "../../helpers/chart_common";
import {formatTimeRange} from "../../helpers/tooltips";

export class ShaperCircuitRtt extends BaseDashlet {
    constructor(slot) {
        super(slot);
        this.rendered = false;
        this.lastGraphState = null;
        this.explainIssues = null;
    }

    title() {
        return "Circuit Round-Trip Time";
    }

    supportsZoom() {
        return true;
    }

    tooltip() {
        return "<h5>Circuit Round-Trip Time</h5><p>Shows the average round-trip time for the circuit in the selected time period.</p>";
    }

    canExplain() {
        return true;
    }

    isExplainReady() {
        return this.explainIssues !== null;
    }

    explain() {
        if (!this.explainIssues || this.explainIssues.length === 0) return null;
        return { region: "Circuit Round-Trip Time", issues: this.explainIssues };
    }

    buildContainer() {
        let base = super.buildContainer();
        base.appendChild(this.graphDiv());
        return base;
    }

    setup() {
        super.setup();
        this.graph = new ShaperCircuitRttGraph(this.graphDivId());
        if (this.lastGraphState !== null) {
            this.graph.update(this.lastGraphState);
        }
    }

    channelRequests() {
        return [ "CircuitDetail" ];
    }

    onMessage(msg) {
        if (msg.Data.target !== "circuit_detail") return;

        if (!this.rendered) {
            let myDiv = document.getElementById(this.id);
            myDiv.classList.add("card", "animate__animated", "animate__fadeIn");
        } else {
            let myDiv = document.getElementById(this.id);
            myDiv.classList.add("animate__animated", "animate__pulse");
            setTimeout(() => {
                myDiv.classList.remove("animate__animated", "animate__pulse");
            }, 1000);
        }
        for (const [key, value] of Object.entries(msg.Data.blob)) {
            this.graph.update(value);
            this.lastGraphState = value;
            this.explainIssues = buildRttIssues(value);
        }
    }

}

function buildRttIssues(data) {
    const series = Array.isArray(data?.median_rtt_median) ? data.median_rtt_median : [];
    const ticks = Array.isArray(data?.ticks) ? data.ticks : [];
    const len = Math.min(series.length, ticks.length);
    if (len < 2) return [];

    const thresholdMs = 160;
    const thresholdRaw = thresholdMs * 10;

    let total = 0;
    let above = 0;
    let firstIdx = -1;
    let lastIdx = -1;
    let maxRaw = null;
    for (let i = 0; i < len; i += 1) {
        const v = series[i];
        if (!Number.isFinite(v)) continue;
        total += 1;
        if (maxRaw === null || v > maxRaw) maxRaw = v;
        if (v >= thresholdRaw) {
            above += 1;
            if (firstIdx === -1) firstIdx = i;
            lastIdx = i;
        }
    }
    if (total === 0 || above === 0) return [];

    const timeRange = (firstIdx >= 0 && lastIdx >= 0)
        ? formatTimeRange(ticks[firstIdx], ticks[lastIdx])
        : null;
    const pct = Math.round((above / total) * 100);
    const peakMs = maxRaw !== null ? (maxRaw / 10).toFixed(0) : null;
    const details = peakMs
        ? `>=${thresholdMs} ms in ${pct}% of samples (peak ${peakMs} ms)`
        : `>=${thresholdMs} ms in ${pct}% of samples`;

    return [
        {
            time_range: timeRange,
            severity: "red",
            issue: "RTT high",
            details,
        },
    ];
}

class ShaperCircuitRttGraph extends DashboardGraph {
    constructor(id) {
        super(id);
        this.option = new GraphOptionsBuilder()
            .withLeftGridSize(75)
            .withTimeAxis()
            .withEmptyLegend()
            .withEmptySeries()
            .build();
        this.option.yAxis = {
            type: "value",
            name: "RTT (ms)",
            nameLocation: "middle",
            nameGap: 50,
            axisLabel: {
                formatter: formatMillisecondsAxis,
            },
        };
        this.option && this.chart.setOption(this.option);
    }

    update(data) {
        this.chart.hideLoading();

        this.option.series = [];
        this.option.xAxis.data = [];
        this.option.legend = { show: false, data: [] };


        // Add series for each shaper
        let d = new MinMaxSeries("RTT", 0);
        for (let x=0; x<data.ticks.length; x++) {
            this.option.xAxis.data.push(data.ticks[x]);
            d.pushPositive(
                data.median_rtt_median[x]/10,
                data.median_rtt_min[x]/10,
                data.median_rtt_max[x]/10,
            )
        }
        d.addToOptions(this.option);

        this.option.tooltip = createBoundedAxisTooltip({
            yFormatter: formatMillisecondsAxis,
            formatter: (params) => {
                if (!params || params.length === 0) return "";
                const timeIndex = params[0].dataIndex;
                return `<div style="font-size: 10px;">
${formatTimeTick(params[0].axisValue)}<br/>
<strong>Round-trip time</strong><br/>
&nbsp;&nbsp;Min: ${formatMillisecondsAxis(data.median_rtt_min[timeIndex] / 10)}<br/>
&nbsp;&nbsp;Median: ${formatMillisecondsAxis(data.median_rtt_median[timeIndex] / 10)}<br/>
&nbsp;&nbsp;Max: ${formatMillisecondsAxis(data.median_rtt_max[timeIndex] / 10)}
</div>`;
            },
        });

        this.chart.setOption(this.option, true);
    }
}
